import { createSlice } from '@reduxjs/toolkit'
import {
  fetchPermissionablePermissions,
  fetchGroups,
  revokePermission,
  createPermission,
  bulkCreatePermissions,
} from '../actions'
import Status from '@constants/request_statuses'
import { SerializableError } from '@app-types/general'
import { Permission, GroupRecord, PermissionRecord, PermissionUIRecord, DestroyablePermissionIds } from '../types'

type PermissionsState = {
  permissions: PermissionRecord
  destroyablePermissionIds: DestroyablePermissionIds
  permissionsUI: PermissionUIRecord
  status: Status
  error: SerializableError | null
  groups: GroupRecord

  fetchGroupsStatus: Status
  fetchGroupsError: SerializableError | null

  createPermissionStatus: Status
  createPermissionError: SerializableError | null
  createPermissionMessage: string | null

  bulkCreatePermissionsStatus: Status
  bulkCreatePermissionsError: SerializableError | null
  bulkCreatePermissionsMessage: string | null

  revokePermissionStatus: Status
  revokePermissionError: SerializableError | null
}

const initialState: PermissionsState = {
  permissions: {},
  destroyablePermissionIds: [],
  permissionsUI: {},
  status: Status.IDLE,
  error: null,
  groups: {},
  fetchGroupsStatus: Status.IDLE,
  fetchGroupsError: null,

  createPermissionStatus: Status.IDLE,
  createPermissionError: null,
  createPermissionMessage: null,

  bulkCreatePermissionsStatus: Status.IDLE,
  bulkCreatePermissionsError: null,
  bulkCreatePermissionsMessage: null,

  revokePermissionStatus: Status.IDLE,
  revokePermissionError: null,
}

const permissionsSlice = createSlice({
  name: 'permissions',
  initialState,
  reducers: {
    resetState: _state => {
      return initialState // Reset state to the initial state
    },
  },
  extraReducers: builder => {
    builder
      // Fetch permissions
      .addCase(fetchPermissionablePermissions.pending, state => {
        state.status = Status.LOADING
        state.error = null
      })
      .addCase(fetchPermissionablePermissions.fulfilled, (state, action) => {
        const payload = action.payload
        state.status = Status.SUCCEEDED
        state.error = null
        state.permissions = payload.normalized.permissions
        state.destroyablePermissionIds = payload.json.meta.destroyable_permission_ids
        state.permissionsUI = payload.json.meta.permissions_ui
      })
      .addCase(fetchPermissionablePermissions.rejected, (state, action) => {
        state.status = Status.FAILED
        state.error = action.payload || null
      })
      // Create permission
      .addCase(createPermission.pending, state => {
        state.createPermissionStatus = Status.LOADING
        state.createPermissionError = null
      })
      .addCase(createPermission.fulfilled, (state, action) => {
        const { normalized, json } = action.payload
        state.permissions = { ...state.permissions, ...normalized.permissions }
        state.destroyablePermissionIds = [...state.destroyablePermissionIds, json.meta.destroyable_permission_id]
        state.permissionsUI = { ...state.permissionsUI, ...json.meta.permission_ui }
        state.createPermissionMessage = json.meta.message
        state.createPermissionStatus = Status.SUCCEEDED
        state.createPermissionError = null
      })
      .addCase(createPermission.rejected, (state, action) => {
        state.createPermissionStatus = Status.FAILED
        state.createPermissionError = action.payload || null
      })
      // Bulk create permissions
      .addCase(bulkCreatePermissions.pending, state => {
        state.bulkCreatePermissionsStatus = Status.LOADING
        state.bulkCreatePermissionsError = null
      })
      .addCase(bulkCreatePermissions.fulfilled, (state, action) => {
        const { normalized, json } = action.payload
        state.bulkCreatePermissionsStatus = Status.SUCCEEDED

        // Merge normalized data into the existing permissions
        state.permissions = { ...state.permissions, ...normalized.permissions }
        state.destroyablePermissionIds = [
          ...state.destroyablePermissionIds,
          ...(json.meta.destroyable_permission_ids || []),
        ]
        state.permissionsUI = { ...state.permissionsUI, ...json.meta.permissions_ui }
        state.bulkCreatePermissionsMessage = json.meta.message

        // Handle potential partial success by checking for errors in meta
        if (json.meta.errors && json.meta.errors.length > 0) {
          // Set a structured error for partial success
          const serializedError: SerializableError = {
            message: json.meta.message,
            fieldErrors: json.meta.errors.map((error: any) => ({
              field: error.meta?.identifier || 'unknown',
              detail: error.detail || error.title || 'Permission creation failed',
            })),
          }
          state.bulkCreatePermissionsError = serializedError
        } else {
          state.bulkCreatePermissionsError = null
        }
      })
      .addCase(bulkCreatePermissions.rejected, (state, action) => {
        state.bulkCreatePermissionsStatus = Status.FAILED
        state.bulkCreatePermissionsError = action.payload || null
      })
      // Revoke permission
      .addCase(revokePermission.pending, state => {
        state.revokePermissionStatus = Status.LOADING
        state.revokePermissionError = null
      })
      .addCase(revokePermission.fulfilled, (state, action) => {
        const { permission_id: revokedPermissionId } = action.payload.json.meta
        state.permissions = Object.keys(state.permissions).reduce(
          (acc, key) => {
            if (key !== revokedPermissionId) {
              acc[key] = state.permissions[key]
            }
            return acc
          },
          {} as Record<string | number, Permission>
        )
        state.destroyablePermissionIds = state.destroyablePermissionIds.filter(id => id !== revokedPermissionId)
        const { [revokedPermissionId]: _, ...restPermissionsUI } = state.permissionsUI
        state.permissionsUI = restPermissionsUI
        state.revokePermissionStatus = Status.SUCCEEDED
        state.revokePermissionError = null
      })
      .addCase(revokePermission.rejected, (state, action) => {
        state.revokePermissionStatus = Status.FAILED
        state.revokePermissionError = action.payload || null
      })
      // Loading groups
      .addCase(fetchGroups.pending, state => {
        state.fetchGroupsStatus = Status.LOADING
        state.fetchGroupsError = null
      })
      .addCase(fetchGroups.fulfilled, (state, action) => {
        const { normalized } = action.payload
        state.fetchGroupsStatus = Status.SUCCEEDED
        state.groups = { ...state.groups, ...normalized.groups }
      })
      .addCase(fetchGroups.rejected, (state, action) => {
        state.fetchGroupsStatus = Status.FAILED
        state.fetchGroupsError = action.payload || null
      })
  },
})

// Selectors
const selectBase = (state: any) => state.resourcePermissions
export const selectPermissionsUI = (state: any) => selectBase(state).permissionsUI
export const selectDestroyablePermissionIds = (state: any) => selectBase(state).destroyablePermissionIds
export const selectPermissions = (state: any) => selectBase(state).permissions
export const selectPermissionsStatus = (state: any) => selectBase(state).status
export const selectPermissionsError = (state: any) => selectBase(state).error

export const selectCreatePermissionStatus = (state: any) => selectBase(state).createPermissionStatus
export const selectCreatePermissionError = (state: any) => selectBase(state).createPermissionError
export const selectCreatePermissionMessage = (state: any) => selectBase(state).createPermissionMessage

export const selectBulkCreatePermissionsStatus = (state: any) => selectBase(state).bulkCreatePermissionsStatus
export const selectBulkCreatePermissionsError = (state: any) => selectBase(state).bulkCreatePermissionsError
export const selectBulkCreatePermissionsMessage = (state: any) => selectBase(state).bulkCreatePermissionsMessage

export const selectRevokePermissionStatus = (state: any) => selectBase(state).revokePermissionStatus
export const selectRevokePermissionError = (state: any) => selectBase(state).revokePermissionError

export const selectGroups = (state: any) => selectBase(state).groups

export const selectFetchGroupsStatus = (state: any) => selectBase(state).fetchGroupsStatus
export const selectFetchGroupsError = (state: any) => selectBase(state).fetchGroupsError

// EXPORTS
export const { resetState } = permissionsSlice.actions
export default permissionsSlice.reducer
